import { FC } from 'react';
import { Alert, Box, Chip, FormControlLabel, Switch, Typography } from '@mui/material';
import { PgPanel } from '../common/PgPanel';

type TEasUpdatesPanelProps = {
	/** Value on screen. */
	paused: boolean;
	/** Saved value. */
	savedPaused: boolean;
	/** EAS updates queued while paused. */
	parkedEasUpdates: number;
	disabled?: boolean;
	onChange: (paused: boolean) => void;
};

const updatesText = (count: number) => `${count} queued EAS update${count === 1 ? '' : 's'}`;

/** "Pause EAS invoice updates": payments stay in the gateway, EAS gets them after the pause. */
export const EasUpdatesPanel: FC<TEasUpdatesPanelProps> = ({
	paused,
	savedPaused,
	parkedEasUpdates,
	disabled,
	onChange,
}) => (
	<PgPanel
		title={
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
				<Typography color="primary" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
					EAS updates
				</Typography>
				{paused !== savedPaused && (
					<Chip size="small" label="Unsaved" color="warning" variant="outlined" />
				)}
				<Chip
					size="small"
					label={paused ? 'Paused' : 'Running'}
					color={paused ? 'error' : 'success'}
				/>
			</Box>
		}
	>
		<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
			Payments are always recorded in the gateway. Normally the paid amount of an invoice is written
			to EAS right away, and EAS core then e-mails the customer &quot;Invoice payment
			received&quot;. While paused, these updates are queued instead and sent to EAS when the pause
			is switched off.
		</Typography>
		<FormControlLabel
			control={
				<Switch
					checked={paused}
					onChange={(e) => onChange(e.target.checked)}
					disabled={disabled}
					color="error"
				/>
			}
			label="Pause EAS invoice updates"
		/>
		<Typography variant="body2" sx={{ mt: 1 }}>
			Queued EAS updates: <b>{parkedEasUpdates}</b>
		</Typography>
		{paused && (
			<Alert severity="warning" sx={{ mt: 2 }}>
				While paused, invoice paid amounts are not written to EAS: EAS shows the invoices paid
				online as unpaid (and sends no &quot;Invoice payment received&quot; e-mails) until the pause
				is switched off.
			</Alert>
		)}
		{savedPaused && !paused && (
			<Alert severity="info" sx={{ mt: 2 }}>
				{parkedEasUpdates > 0
					? `On save, the ${updatesText(parkedEasUpdates)} will be sent to EAS.`
					: 'On save, EAS updates resume.'}
			</Alert>
		)}
	</PgPanel>
);
